import * as gtag from "../../../lib/gtag"
import CheckAlreadyRegister from "../utils/CheckAlreadyRegister"
import { useCallback } from "react"
import { isPhoneNumberCorrect } from "../../incheon/validations"
import { formatRegistrationNumber } from "@today/lib"
import RegisteringOrSuccess from "../utils/RegisteringOrSuccess"
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaTimesCircle,
} from "react-icons/fa"
import { FormControl } from "baseui/form-control"
import { Label } from "../../incheon/Label"
import { Input } from "baseui/input"
import { Button } from "baseui/button"
import { Modal, ModalBody, ModalFooter } from "baseui/modal"
import useSignup, { Step } from "../../../hooks/useSignup"
import RegisterTitle from "../commoon/RegisterTitle"
import { MusinsaRegistrationTerm } from "./MusinsaRegistrationTerm"

export function MusinsaRegistrationPage() {
  const {
    // State 값들
    currentStep,
    registrationNumber,
    companyName,
    companyNameStatus,
    invalidRegistrationNumberErrorMessage,
    transactionId,
    verificationCode,
    hasPhoneNumberVerified,
    verificationCodeErrorMessage,
    password,
    hasPasswordChecked,
    repeatedPassword,
    hasRepeatedPasswordChecked,
    showPasswordsMismatched,
    hasAgreedTerm,
    hasReachedEndOfTerm,
    phoneNumber,
    registerProcess,
    currentFocus,
    companyRepresentativeName,
    address,
    addressError,
    serviceName,

    // 변수
    formValidation,
    formattedRegisterNumber,
    formattedPhoneNumber,
    isGoodPassword,
    passwordErrorMessage,

    // Ref 값들
    scrollTermRef,
    inputCompanyNameRef,
    inputPhoneRef,
    inputRegistrationNumberRef,
    inputVerificationCodeRef,
    inputPasswordRef,
    inputRepeatPasswordRef,
    buttonReadTermRef,
    buttonRegisterRef,
    inputCompanyRepresentativeNameRef,
    inputAddressRef,
    inputNameRef,

    // 기존 함수들
    formChange,
    verifyPhoneNumber,
    setCurrentStep,
    setCurrentFocus,
    setHasPasswordChecked,
    setHasRepeatedPasswordChecked,
    setShowPasswordsMismatched,
    inputOverrides,
    sendEvent,
    setHasAgreedTerm,
    register,
    setCompanyNameStatus,
    checkAddress,
  } = useSignup()

  const isDisabled = useCallback(() => {
    if (currentStep === Step.InputCustomCompanyName) {
      return !companyName
    }

    if (currentStep === Step.InputServiceName) {
      return !serviceName
    }

    if (currentStep === Step.CompanyRepresentativeName) {
      return !companyRepresentativeName
    }

    if (currentStep === Step.InputPassword) {
      return !isGoodPassword
    }

    if (currentStep === Step.AgreeTerm) {
      return !hasReachedEndOfTerm
    }

    if (currentStep === Step.BeforeReadingTerm) {
      return false
    }

    if (currentStep === Step.AllDone) {
      return !formValidation
    }

    return true
  }, [
    currentStep,
    companyName,
    companyRepresentativeName,
    hasReachedEndOfTerm,
    formValidation,
    isGoodPassword,
    serviceName,
  ])

  return (
    <CheckAlreadyRegister type="signup">
      <RegisteringOrSuccess
        registerProcess={registerProcess}
        companyName={companyName}
      >
        <RegisterTitle
          companyName={companyName}
          companyNameStatus={companyNameStatus}
          currentStep={currentStep}
          formValidation={formValidation}
        />
        <div className="relative flex w-full flex-1 flex-col overflow-y-hidden">
          <div className="w-full overflow-y-scroll">
            <div className="flex h-full flex-col-reverse justify-end md:flex-col md:justify-start">
              <div>
                <FormControl
                  label={
                    <Label
                      bold={currentFocus === "registrationNumber"}
                      tip="(10자리 숫자)"
                    >
                      사업자등록번호
                    </Label>
                  }
                  caption={
                    currentStep === Step.InputRegistrationNumber &&
                    invalidRegistrationNumberErrorMessage && (
                      <p className="text-red-500">
                        * {invalidRegistrationNumberErrorMessage}
                      </p>
                    )
                  }
                >
                  <Input
                    inputRef={inputRegistrationNumberRef}
                    autoComplete="username"
                    inputMode="numeric"
                    id="registrationNumber"
                    disabled={
                      currentStep > Step.ConfirmCompanyInfo &&
                      !!companyNameStatus
                    }
                    maxLength={12}
                    required
                    value={formattedRegisterNumber}
                    onChange={formChange}
                    onFocus={() => setCurrentFocus("registrationNumber")}
                    overrides={inputOverrides("registrationNumber")}
                    endEnhancer={
                      currentStep === Step.LoadingCompanyInfo ? (
                        <FaSpinner className="animate-spin" />
                      ) : currentStep === Step.InputCustomCompanyName ? (
                        <FaExclamationTriangle className="text-yellow-500" />
                      ) : !!companyNameStatus ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : currentStep === Step.InputRegistrationNumber &&
                        invalidRegistrationNumberErrorMessage ? (
                        <FaTimesCircle className="text-red-500" />
                      ) : null
                    }
                  />
                </FormControl>
              </div>
              <div
                className={
                  currentStep === Step.InputCustomCompanyName ||
                  companyNameStatus === "CUSTOM" ||
                  companyNameStatus === "CUSTOM_CONFIRMED"
                    ? "animate-fadeIn block md:animate-none"
                    : "hidden"
                }
              >
                <FormControl
                  label={
                    <Label
                      bold={currentFocus === "companyName"}
                      tip="(사업자등록증에 기재된 사업자명)"
                    >
                      사업자명
                    </Label>
                  }
                >
                  <Input
                    inputRef={inputCompanyNameRef}
                    autoComplete="organization"
                    value={companyName}
                    id="companyName"
                    disabled={
                      currentStep > Step.InputCustomCompanyName &&
                      companyNameStatus !== "CUSTOM"
                    }
                    required
                    onChange={formChange}
                    onFocus={() => setCurrentFocus("companyName")}
                    overrides={inputOverrides("companyName")}
                  />
                </FormControl>
              </div>
              <div
                className={
                  currentStep >= Step.InputServiceName
                    ? "animate-fadeIn block md:animate-none"
                    : "hidden md:block"
                }
              >
                <FormControl
                  label={
                    <Label
                      bold
                      tip="(운송장 출력 및 고객 알림톡 발송 시 본 서비스명이 사용됩니다)"
                    >
                      서비스명
                    </Label>
                  }
                >
                  <Input
                    inputRef={inputNameRef}
                    id="serviceName"
                    type="text"
                    value={serviceName}
                    onChange={formChange}
                  />
                </FormControl>
              </div>
              <div
                className={
                  currentStep >= Step.CompanyRepresentativeName
                    ? "animate-fadeIn block md:animate-none"
                    : "hidden md:block"
                }
              >
                <FormControl label={<Label bold>대표자명</Label>}>
                  <Input
                    inputRef={inputCompanyRepresentativeNameRef}
                    id="companyRepresentativeName"
                    type="text"
                    value={companyRepresentativeName}
                    onChange={formChange}
                  />
                </FormControl>
              </div>
              <div
                className={
                  currentStep >= Step.InputAddress
                    ? "animate-fadeIn block md:animate-none"
                    : "hidden md:block"
                }
              >
                <FormControl
                  label={
                    <Label bold tip="(픽업택배 방문용 상세주소를 입력해주세요)">
                      사업장 주소
                    </Label>
                  }
                  caption={addressError && errorCaption(addressError)}
                >
                  <Input
                    inputRef={inputAddressRef}
                    id="address"
                    type="text"
                    value={address}
                    onChange={formChange}
                    onBlur={() => checkAddress()}
                    required
                    endEnhancer={
                      addressError && <FeedbackIcon success={!addressError} />
                    }
                  />
                </FormControl>
              </div>
              <div
                className={
                  currentStep >= Step.InputPhoneNumber
                    ? "animate-fadeIn block md:animate-none"
                    : "hidden md:block"
                }
              >
                <FormControl
                  label={
                    <Label bold={currentFocus === "phone"}>
                      담당자 휴대전화
                    </Label>
                  }
                >
                  <Input
                    inputRef={inputPhoneRef}
                    autoComplete="mobile tel-national"
                    id="phoneNumber"
                    type="tel"
                    maxLength={13}
                    inputMode="tel"
                    value={formattedPhoneNumber}
                    disabled={
                      currentStep >= Step.InputPassword && transactionId
                    }
                    required
                    onChange={formChange}
                    onFocus={() => setCurrentFocus("phone")}
                    overrides={inputOverrides("phone")}
                    endEnhancer={
                      transactionId ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : currentStep === Step.InputPhoneNumber &&
                        !isPhoneNumberCorrect(phoneNumber) ? (
                        phoneNumber.length >= 10 && (
                          <FaExclamationTriangle className="text-yellow-500" />
                        )
                      ) : isPhoneNumberCorrect(phoneNumber) ? (
                        <Button
                          className="h-8 w-14"
                          size="mini"
                          isLoading={
                            currentStep ===
                            Step.SendingPhoneNumberVerificationKey
                          }
                          onClick={() => {
                            verifyPhoneNumber(phoneNumber)
                          }}
                        >
                          {currentStep === Step.InputPhoneNumberVerificationKey
                            ? "재전송"
                            : "인증"}
                        </Button>
                      ) : null
                    }
                  />
                </FormControl>
              </div>
              <div
                className={
                  currentStep === Step.InputPhoneNumberVerificationKey ||
                  currentStep === Step.CheckingPhoneNumberVerificationKey ||
                  (!transactionId && hasPhoneNumberVerified)
                    ? "animate-fadeIn block md:animate-none"
                    : "hidden"
                }
              >
                <FormControl
                  label={
                    <Label
                      bold={currentFocus === "verificationCode"}
                      tip={
                        <span className="text-red-500">
                          (카카오톡 또는 SMS를 확인해주세요)
                        </span>
                      }
                    >
                      6자리 인증코드 입력
                    </Label>
                  }
                  caption={
                    verificationCodeErrorMessage && (
                      <p className="text-red-500">
                        * {verificationCodeErrorMessage}
                      </p>
                    )
                  }
                >
                  <Input
                    inputRef={inputVerificationCodeRef}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    id="verificationCode"
                    value={verificationCode}
                    maxLength={6}
                    onChange={formChange}
                    onFocus={() => setCurrentFocus("verificationCode")}
                    required
                    overrides={inputOverrides("verificationCode")}
                    endEnhancer={
                      currentStep ===
                      Step.CheckingPhoneNumberVerificationKey ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        hasPhoneNumberVerified &&
                        verificationCode.length === 6 && (
                          <FaTimesCircle className="text-red-500" />
                        )
                      )
                    }
                  />
                </FormControl>
              </div>
              <div
                className={
                  currentStep >= Step.InputPassword
                    ? "animate-fadeIn block md:animate-none"
                    : "hidden md:block"
                }
              >
                <FormControl
                  label={
                    <Label bold={currentFocus === "password"}>비밀번호</Label>
                  }
                  caption={
                    hasPasswordChecked &&
                    password.length > 0 &&
                    !isGoodPassword && (
                      <p className="text-red-500">* {passwordErrorMessage}</p>
                    )
                  }
                >
                  <Input
                    inputRef={inputPasswordRef}
                    autoComplete="new-password"
                    value={password}
                    id="password"
                    type="password"
                    onChange={formChange}
                    onFocus={() => setCurrentFocus("password")}
                    required
                    onBlur={() => {
                      if (password.length > 0) {
                        setHasPasswordChecked(true)
                      }
                    }}
                    overrides={inputOverrides("password")}
                    endEnhancer={
                      isGoodPassword ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : (
                        password.length > 0 && (
                          <FaExclamationTriangle className="text-yellow-500" />
                        )
                      )
                    }
                  />
                </FormControl>
              </div>
              <div
                className={
                  currentStep >= Step.RepeatPassword
                    ? "animate-fadeIn block md:animate-none"
                    : "hidden md:block"
                }
              >
                <FormControl
                  label={
                    <Label
                      bold={currentFocus === "repeatingPassword"}
                      tip={
                        <>
                          <span className="md:hidden">
                            (아래 입력하신 비밀번호를 한 번 더 입력해주세요)
                          </span>
                          <span className="hidden md:inline">
                            (위에 입력하신 비밀번호를 한 번 더 입력해주세요)
                          </span>
                        </>
                      }
                    >
                      비밀번호 확인
                    </Label>
                  }
                  caption={
                    showPasswordsMismatched && (
                      <p className="text-red-500">
                        * 비밀번호가 일치하지 않습니다.
                      </p>
                    )
                  }
                >
                  <Input
                    inputRef={inputRepeatPasswordRef}
                    autoComplete="off"
                    value={repeatedPassword}
                    id="repeatedPassword"
                    type="password"
                    onChange={formChange}
                    onFocus={() => setCurrentFocus("repeatingPassword")}
                    required
                    onBlur={() => {
                      if (repeatedPassword.length === 0) {
                        return
                      }
                      setHasRepeatedPasswordChecked(true)
                      if (password !== repeatedPassword) {
                        setShowPasswordsMismatched(true)
                      }
                    }}
                    overrides={inputOverrides("password")}
                    endEnhancer={
                      password.length === 0 ? null : repeatedPassword ===
                        password ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : (
                        hasRepeatedPasswordChecked && (
                          <FaTimesCircle className="text-red-500" />
                        )
                      )
                    }
                  />
                </FormControl>
              </div>
              <div
                className={
                  currentStep >= Step.BeforeReadingTerm
                    ? "animate-fadeIn block md:animate-none"
                    : "hidden md:block"
                }
              >
                <FormControl
                  label={
                    <Label bold={currentFocus === "readTerm"}>약관 동의</Label>
                  }
                >
                  <Button
                    ref={buttonReadTermRef}
                    className="w-full focus:outline-none focus:ring-0 focus:ring-offset-0"
                    size="compact"
                    kind="secondary"
                    onFocus={() => setCurrentFocus("readTerm")}
                    onClick={() => setCurrentStep(Step.AgreeTerm)}
                    endEnhancer={
                      hasAgreedTerm ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : null
                    }
                  >
                    이용약관 읽기
                    {hasAgreedTerm && (
                      <span className="ml-1 font-bold">[동의완료]</span>
                    )}
                  </Button>
                </FormControl>
              </div>
            </div>
            {currentStep === Step.AgreeTerm && (
              <div className="animate-slideLeft absolute left-0 top-0 h-full w-full border border-gray-900 bg-white p-3">
                <MusinsaRegistrationTerm ref={scrollTermRef} />
              </div>
            )}
          </div>
        </div>
        <div className="flex w-full">
          <Button
            ref={buttonRegisterRef}
            className="flex-grow shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0 md:hidden"
            disabled={isDisabled()}
            onClick={() => {
              switch (currentStep) {
                case Step.InputCustomCompanyName:
                  if (companyName) {
                    setCurrentStep(Step.ConfirmCompanyInfo)
                  }
                  break
                case Step.InputServiceName:
                  if (serviceName) {
                    setCurrentStep(Step.CompanyRepresentativeName)
                  }
                  break
                case Step.CompanyRepresentativeName:
                  if (companyRepresentativeName) {
                    setCurrentStep(Step.InputAddress)
                  }
                  break
                case Step.InputPassword:
                  if (isGoodPassword) {
                    setCurrentStep(Step.RepeatPassword)
                  }
                  break
                case Step.BeforeReadingTerm:
                  setCurrentStep(Step.AgreeTerm)
                  break
                case Step.AgreeTerm:
                  if (hasReachedEndOfTerm) {
                    sendEvent(
                      "click_button",
                      "term_agreement",
                      registrationNumber
                    )
                    setHasAgreedTerm(true)
                    setCurrentStep(Step.AllDone)
                  }
                  break
                case Step.AllDone:
                  sendEvent(
                    "click_button",
                    "submit_registration",
                    registrationNumber
                  )
                  if (formValidation) {
                    register()
                  }
              }
            }}
          >
            {currentStep === Step.AgreeTerm
              ? hasReachedEndOfTerm
                ? "동의합니다"
                : "약관을 끝까지 읽어주세요"
              : currentStep === Step.AllDone
              ? "회원가입 하기"
              : "다음"}
          </Button>
          <Button
            className="hidden flex-grow md:block"
            disabled={
              (currentStep !== Step.AgreeTerm && !formValidation) ||
              (currentStep === Step.AgreeTerm && !hasReachedEndOfTerm)
            }
            onClick={() => {
              if (currentStep === Step.AgreeTerm) {
                if (hasReachedEndOfTerm) {
                  gtag.event({
                    action: "click_term_agreement_button",
                    category: "Musinsa",
                    label: formatRegistrationNumber(registrationNumber) ?? "",
                    value: formValidation ? 1 : 0,
                  })
                  setHasAgreedTerm(true)
                  setCurrentStep(Step.AllDone)
                }
                return
              } else if (formValidation) {
                gtag.event({
                  action: "click_register_button",
                  category: "Musinsa",
                  label: formValidation ? "can_register" : "cannot_register",
                  value: 1,
                })
                register()
              }
            }}
          >
            {currentStep === Step.AgreeTerm
              ? hasReachedEndOfTerm
                ? "동의합니다"
                : "약관을 끝까지 읽어주세요"
              : "회원가입 하기"}
          </Button>
        </div>
        {currentStep === Step.ConfirmCompanyInfo && (
          <>
            <div className="absolute hidden md:block">
              <Modal
                isOpen={currentStep === Step.ConfirmCompanyInfo}
                closeable
                onClose={() => setCurrentStep(Step.InputRegistrationNumber)}
                overrides={{
                  Dialog: {
                    props: {
                      className: "hidden md:block animate-fadeIn",
                    },
                  },
                }}
              >
                <ModalBody className="pt-6">
                  <p className="mb-4 text-center text-lg">
                    사업장 이름을 확인해주세요.
                  </p>
                  <p className="text-center text-2xl font-semibold">
                    &ldquo;{companyName}&rdquo;
                  </p>
                </ModalBody>
                <ModalFooter className="text-center">
                  <Button
                    className="min-w-[80px] shadow-none"
                    onClick={() => {
                      setCompanyNameStatus(
                        companyNameStatus === "CUSTOM"
                          ? "CUSTOM_CONFIRMED"
                          : "AUTOMATIC_CONFIRMED"
                      )
                      setCurrentStep(Step.InputServiceName)
                    }}
                  >
                    확인
                  </Button>
                </ModalFooter>
              </Modal>
            </div>
            <div className="md:hidden">
              <div
                className="fixed left-0 top-0 z-40 h-screen w-screen bg-gray-800 bg-opacity-80 md:hidden"
                style={{ height: "100dvh" }}
                onClick={() => setCurrentStep(Step.InputRegistrationNumber)}
              />
              <div className="animate-slideUp fixed bottom-0 left-0 z-50 flex w-screen flex-col rounded-t-2xl bg-white px-4 pb-6 pt-8 md:hidden">
                <p className="mb-4 text-center text-lg">
                  사업장 이름을 확인해주세요.
                </p>
                <p className="mb-6 text-center text-2xl font-semibold">
                  &ldquo;{companyName}&rdquo;
                </p>
                <Button
                  className="shadow-none"
                  onClick={() => {
                    setCompanyNameStatus(
                      companyNameStatus === "CUSTOM"
                        ? "CUSTOM_CONFIRMED"
                        : "AUTOMATIC_CONFIRMED"
                    )
                    setCurrentStep(Step.InputServiceName)
                  }}
                >
                  확인
                </Button>
              </div>
            </div>
          </>
        )}
      </RegisteringOrSuccess>
    </CheckAlreadyRegister>
  )
}

function errorCaption(errorMessage: string) {
  return <p className="text-red-500">* {errorMessage}</p>
}

function FeedbackIcon({ success }: { success: boolean }) {
  return success ? (
    <FaCheckCircle className="text-green-500" />
  ) : (
    <FaTimesCircle className="text-red-500" />
  )
}
